import { supabase, db } from '@/lib/supabase';
import { appFetch } from '@/lib/appApi';
import slugify from 'slugify';
import { normalizeCategorySlug } from '@/lib/storefront/categoryUrl';

const isAbortError = (err: unknown): boolean => {
    const anyErr = err as any;
    const msg = String(anyErr?.message || anyErr?.details || '');
    return anyErr?.name === 'AbortError' || msg.includes('AbortError') || msg.includes('signal is aborted');
};

const sanitizeCategorySlugInput = (raw: unknown): string => {
    const base = String(raw ?? '').trim();
    // Keep only URL-safe form and normalize lowercase.
    const sanitized = slugify(base, { lower: true, strict: true, locale: 'fr' }).trim();
    const normalized = normalizeCategorySlug(sanitized);
    if (!normalized) {
        throw new Error('Slug de catégorie invalide. Veuillez entrer un slug non vide.');
    }
    return normalized;
};

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export interface Category {
    id: string;
    store_id: string;
    parent_id: string | null;
    name: string;
    slug: string;
    description: string | null;
    image_url: string | null;
    position: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    /** Computed client-side from the products array (not a DB column) */
    product_count?: number;
}

export interface CreateCategoryData {
    name: string;
    slug: string;
    description?: string | null;
    image_url?: string | null;
    parent_id?: string | null;
    position?: number;
    is_active?: boolean;
}

export interface UpdateCategoryData {
    name?: string;
    slug?: string;
    description?: string | null;
    image_url?: string | null;
    parent_id?: string | null;
    position?: number;
    is_active?: boolean;
}

// ─────────────────────────────────────────────
// Service
// ─────────────────────────────────────────────

export const categoriesService = {
    /**
     * Fetch all categories for a store, ordered by position then name.
     * Used by both dashboard and storefront.
     */
    async getByStore(storeId: string): Promise<Category[]> {
        if (!storeId) return [];

        try {
            const { data, error } = await db
                .from('categories')
                .select('*')
                .eq('store_id', storeId)
                .order('position', { ascending: true })
                .order('name', { ascending: true });

            if (error) {
                // supabase-js sometimes surfaces aborts as thrown exceptions, but be defensive anyway
                if (isAbortError(error)) return [];
                console.error('[categoriesService] getByStore error:', error.message);
                return [];
            }

            return data ?? [];
        } catch (err) {
            if (isAbortError(err)) return [];
            console.error('[categoriesService] getByStore error:', (err as any)?.message ?? String(err));
            return [];
        }
    },

    /**
   * Fetch a single category by slug (for public storefront category pages).
   * Case-insensitive: "Shoes" and "shoes" both match.
   * Only returns active categories.
   */
    async getBySlug(storeId: string, slug: string): Promise<Category | null> {
        if (!storeId || !slug) return null;
        const safeSlug = normalizeCategorySlug(slug);
        if (!safeSlug) return null;

        try {
            const { data, error } = await db
                .from('categories')
                .select('*')
                .eq('store_id', storeId)
                .ilike('slug', safeSlug) // case-insensitive match — uses the LOWER(slug) index
                .eq('is_active', true)
                .single();

            if (error) {
                if (isAbortError(error)) return null;
                if (error.code !== 'PGRST116') {
                    console.error('[categoriesService] getBySlug error:', error.message);
                }
                return null;
            }

            return data ?? null;
        } catch (err) {
            if (isAbortError(err)) return null;
            console.error('[categoriesService] getBySlug error:', (err as any)?.message ?? String(err));
            return null;
        }
    },

    /**
     * Create a new category for a store.
     */
    async create(storeId: string, data: CreateCategoryData): Promise<Category> {
        const slug = sanitizeCategorySlugInput(data.slug || data.name);
        const available = await this.isSlugAvailable(storeId, slug);
        if (!available) {
            throw new Error('Ce slug de catégorie existe déjà dans votre boutique.');
        }

        const { data: created, error } = await db
            .from('categories')
            .insert({
                store_id: storeId,
                ...data,
                slug,
                is_active: data.is_active ?? true,
                position: data.position ?? 0,
            })
            .select()
            .single();

        if (error) throw new Error(error.message);
        return created;
    },

    /**
     * Update an existing category.
     */
    async update(id: string, data: UpdateCategoryData): Promise<Category> {
        const payload: UpdateCategoryData = { ...data };
        if (data.slug !== undefined) {
            const { data: current, error: currentError } = await db
                .from('categories')
                .select('store_id')
                .eq('id', id)
                .single();
            if (currentError || !current?.store_id) {
                throw new Error('Catégorie introuvable pour valider le slug.');
            }
            const safeSlug = sanitizeCategorySlugInput(data.slug);
            const available = await this.isSlugAvailable(current.store_id, safeSlug, id);
            if (!available) {
                throw new Error('Ce slug de catégorie existe déjà dans votre boutique.');
            }
            payload.slug = safeSlug;
        }

        const { data: updated, error } = await db
            .from('categories')
            .update(payload)
            .eq('id', id)
            .select()
            .single();

        if (error) throw new Error(error.message);
        return updated;
    },

    /**
     * Delete a category by ID.
     * Products that belonged to this category will have category_id set to NULL
     * (handled by the ON DELETE SET NULL FK constraint).
     */
    async delete(id: string): Promise<void> {
        const { error } = await db
            .from('categories')
            .delete()
            .eq('id', id);

        if (error) throw new Error(error.message);
    },

    /**
     * Reorder categories by updating their positions in sequence.
     * Accepts an array of category IDs in the desired order.
     */
    async reorder(orderedIds: string[]): Promise<void> {
        const updates = orderedIds.map((id, index) => ({
            id,
            position: index,
        }))

        await Promise.all(
            updates.map(async (upd) => {
                const { error } = await db
                    .from('categories')
                    .update({ position: upd.position })
                    .eq('id', upd.id)
                if (error) {
                    console.error('[categoriesService] reorder error:', error.message)
                }
            })
        )
    },

    /**
   * Check if a slug is already in use for a given store (for form validation).
   */
    async isSlugAvailable(storeId: string, slug: string, excludeId?: string): Promise<boolean> {
        const safeSlug = sanitizeCategorySlugInput(slug);
        let query = db
            .from('categories')
            .select('id')
            .eq('store_id', storeId)
            .ilike('slug', safeSlug); // case-insensitive check

        if (excludeId) {
            query = query.neq('id', excludeId);
        }

        const { data } = await query;
        return !data || data.length === 0;
    },

    /**
     * Fetch live product counts per category for a store.
     * Returns a Map<category_id, count> for quick lookups.
     * Uses the get_category_product_counts RPC (avoids denormalized columns).
     */
    async getProductCounts(storeId: string): Promise<Map<string, number>> {
        if (!storeId) return new Map();

        try {
            const res = await appFetch<{ ok: boolean; data: { category_id: string; product_count: number }[] }>(
                '/api/app/categories/product-counts',
                { method: 'POST', json: { storeId } },
            );
            if (!res.ok) return new Map();
            return new Map(
                (res.data ?? []).map((row) => [row.category_id, row.product_count]),
            );
        } catch (e) {
            console.error('[categoriesService] getProductCounts error:', e);
            return new Map();
        }
    },
};
